import {
  A,
  Navigate,
  Route,
  Router,
  useLocation,
  useNavigate,
  type RouteSectionProps,
} from "@solidjs/router";
import {
  createEffect,
  createMemo,
  createResource,
  createSignal,
  For,
  Match,
  onCleanup,
  onMount,
  Show,
  Switch,
  type JSX,
} from "solid-js";
import type { Locale } from "@energyplatform/i18n";
import { locale, SUPPORTED_LOCALES, t } from "./i18n.ts";
import { chooseLanguage } from "./preferences.ts";
import { offers } from "./access.ts";
import { api, liveState, session, signInUrl } from "./services.ts";
import { subtreeOf, treeRows } from "./tree.ts";
import { pointState } from "./assets.ts";
import {
  pathTo,
  period,
  PERIODS,
  range,
  scope,
  setPeriod,
  setScope,
  siteTree,
  type Period,
} from "./context.ts";
import { formatRange } from "./format.ts";
import { Icon, type IconName } from "./icons.tsx";
import { NodeKindIcon } from "./components/Media.tsx";
import { ErrorBox } from "./components/ErrorBox.tsx";
import { operator } from "./components/ui.tsx";
import { profilesFor, type ProfileId } from "./profiles.ts";
import { setViewProfile, viewProfile } from "./view.ts";
import { OverviewPage } from "./pages/OverviewPage.tsx";
import { FlowsPage } from "./pages/FlowsPage.tsx";
import { EventsPage } from "./pages/EventsPage.tsx";
import { EventPage } from "./pages/EventPage.tsx";
import { Bell } from "./components/Bell.tsx";
import { CommissioningPage } from "./pages/CommissioningPage.tsx";
import { PlansPage } from "./pages/PlansPage.tsx";
import { ReportsPage } from "./pages/ReportsPage.tsx";
import { FinancePage } from "./pages/FinancePage.tsx";
import { OptimizationPage } from "./pages/OptimizationPage.tsx";
import { UsersPage } from "./pages/UsersPage.tsx";
import { ProfilePage } from "./pages/ProfilePage.tsx";
import { PlacesPage } from "./pages/PlacesPage.tsx";
import { AutomationsPage } from "./pages/AutomationsPage.tsx";
import { ClusterPage } from "./pages/ClusterPage.tsx";
import { ChargingPage } from "./pages/ChargingPage.tsx";
import { AnalysisPage } from "./pages/AnalysisPage.tsx";
import { AssetsPage } from "./pages/AssetsPage.tsx";
import { DevicesPage } from "./pages/DevicesPage.tsx";
import { ConnectionsPage } from "./pages/ConnectionsPage.tsx";
import { ExtensionsPage } from "./pages/ExtensionsPage.tsx";
import { CatalogPage } from "./pages/CatalogPage.tsx";

interface NavItem {
  path: string;
  key: string;
  icon: IconName;
  /** Screens that look at a part of the tree over a period show the scope and period in the context bar. */
  scoped: boolean;
}

/** Every screen; the view profile decides which of them the navigation offers and in which sections. */
const ALL: NavItem[] = [
  { path: "/places", key: "places", icon: "home", scoped: true },
  { path: "/overview", key: "overview", icon: "chart", scoped: true },
  { path: "/flows", key: "flows", icon: "flow", scoped: true },
  { path: "/events", key: "events", icon: "bell", scoped: true },
  {
    path: "/commissioning",
    key: "commissioningScreen",
    icon: "check",
    scoped: true,
  },
  { path: "/analysis", key: "analyses", icon: "spark", scoped: true },
  { path: "/automations", key: "automations", icon: "auto", scoped: false },
  { path: "/cluster", key: "cluster", icon: "link", scoped: false },
  { path: "/charging", key: "charging", icon: "plug", scoped: false },
  { path: "/plans", key: "plansMaps", icon: "map", scoped: true },
  { path: "/reports", key: "reports", icon: "file", scoped: true },
  { path: "/finance", key: "finance", icon: "coins", scoped: true },
  { path: "/optimization", key: "optimization", icon: "target", scoped: true },
  { path: "/assets", key: "assets", icon: "tree", scoped: true },
  { path: "/devices", key: "devices", icon: "gauge", scoped: true },
  { path: "/integrations", key: "integrations", icon: "plug", scoped: false },
  { path: "/users", key: "users", icon: "users", scoped: false },
  { path: "/extensions", key: "extensions", icon: "folder", scoped: false },
  { path: "/catalog", key: "catalog", icon: "list", scoped: false },
];
// Only the screens the person's access allows; the host refuses the rest anyway.
const itemsOf = (paths: readonly string[]) =>
  paths
    .filter(offers)
    .map((p) => ALL.find((i) => i.path === p))
    .filter((i): i is NavItem => i !== undefined);

const current = (pathname: string) =>
  ALL.find((i) => pathname.startsWith(i.path));

function NavLinks(props: { onNavigate?: () => void }) {
  const link = (item: NavItem) => (
    <A href={item.path} onClick={() => props.onNavigate?.()}>
      <Icon name={item.icon} />
      <span>{t(`nav.${item.key}`)}</span>
    </A>
  );
  return (
    <nav class="nav" aria-label={t("shell.navigation")}>
      <For each={viewProfile().sections}>
        {(section) => (
          <>
            <div class="sect">{t(`nav.${section.key}`)}</div>
            <For each={itemsOf(section.paths)}>{link}</For>
          </>
        )}
      </For>
    </nav>
  );
}

function LanguageSelect(props: { id: string }) {
  return (
    <select
      id={props.id}
      value={locale()}
      onChange={(e) => void chooseLanguage(e.currentTarget.value as Locale)}
    >
      <For each={SUPPORTED_LOCALES}>
        {(l) => <option value={l}>{t(`lang.${l}`)}</option>}
      </For>
    </select>
  );
}

/** View profile switch; shown only when more than one profile is offered to the user. */
function ProfileSelect(props: { id: string; onChanged?: () => void }) {
  const navigate = useNavigate();
  const offered = () => profilesFor(operator());
  return (
    <Show when={offered().length > 1}>
      <label for={props.id}>{t("profile.label")}</label>
      <select
        id={props.id}
        value={viewProfile().id}
        title={t(`profile.hint.${viewProfile().id}`)}
        onChange={(e) => {
          setViewProfile(e.currentTarget.value as ProfileId);
          navigate(viewProfile().start);
          props.onChanged?.();
        }}
      >
        <For each={offered()}>
          {(p) => <option value={p.id}>{t(`profile.${p.id}`)}</option>}
        </For>
      </select>
    </Show>
  );
}

function SignOut() {
  // A plain form POST: the host ends the session and sends the browser to ZITADEL to sign out there too.
  return (
    <form method="post" action="/auth/logout">
      <button type="submit" class="btn ghost sm">
        {t("auth.signOut")}
      </button>
    </form>
  );
}

function Brand() {
  return (
    <div class="brand">
      <div class="brand-mark">
        <Icon name="flow" />
      </div>
      <div>
        <b>{t("app.title")}</b>
        <span>{t("app.subtitle")}</span>
      </div>
    </div>
  );
}

function Rail() {
  const user = () => {
    const s = session();
    return s.status === "signedIn" ? s.user : undefined;
  };
  return (
    <aside class="rail">
      <Brand />
      <NavLinks />
      <div class="who">
        <ProfileSelect id="profileSel" />
        <label for="langSel">{t("lang.label")}</label>
        <LanguageSelect id="langSel" />
        <Show when={user()}>
          {(u) => (
            <div class="user">
              <A href="/profile" title={t("profilePage.open")}>
                {u().name ?? u().email}
              </A>
              <SignOut />
            </div>
          )}
        </Show>
        <small>{t(`role.${user()?.roles[0] ?? "none"}`)}</small>
      </div>
    </aside>
  );
}

/** Popover state that closes on an outside click and on Escape. */
function usePopover(): [
  () => boolean,
  (open: boolean) => void,
  (el: HTMLElement) => void,
] {
  const [open, setOpen] = createSignal(false);
  let root: HTMLElement | undefined;
  const onDoc = (e: MouseEvent) => {
    if (open() && root && !root.contains(e.target as Node)) {
      setOpen(false);
    }
  };
  const onKey = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      setOpen(false);
    }
  };
  onMount(() => {
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
  });
  onCleanup(() => {
    document.removeEventListener("mousedown", onDoc);
    document.removeEventListener("keydown", onKey);
  });
  return [open, setOpen, (el) => (root = el)];
}

/** Tree guides (├ └ │) before a row, from whether each level is the last of its siblings. */
function Guides(props: { lasts: boolean[] }) {
  return (
    <Show when={props.lasts.length > 0}>
      <span class="guides" aria-hidden="true">
        <For each={props.lasts}>
          {(last, k) => (
            <span
              class={`g ${k() === props.lasts.length - 1 ? (last ? "l" : "t") : last ? "" : "v"}`}
            />
          )}
        </For>
      </span>
    </Show>
  );
}

/**
 * Where you are (prototype context bar): the path of the scope, and a tree to choose another one with a search,
 * point counts and places that need attention. "/" opens it.
 */
function ScopePicker() {
  const [open, setOpen, ref] = usePopover();
  const [query, setQuery] = createSignal("");
  const nodes = () => siteTree()?.nodes ?? [];
  const points = () => siteTree()?.points ?? [];
  const crumbs = () => pathTo(nodes(), scope()?.id);
  const rows = createMemo(() => treeRows(nodes(), query()));
  // Data state is fetched when the picker opens, for the dots of places that need attention.
  const [readings] = createResource(
    () =>
      open()
        ? nodes()
            .filter((n) => n.parentId === null)
            .map((n) => n.id)
            .join()
        : undefined,
    async (ids) =>
      new Map(
        (await Promise.all(ids.split(",").map((id) => api.sites.pointsNow(id))))
          .flat()
          .map((r) => [r.pointId, r]),
      ),
  );
  const under = (id: string) => {
    const inside = new Set(subtreeOf(nodes(), id));
    return points().filter((p) => inside.has(p.nodeId));
  };
  const attention = (id: string) =>
    readings() !== undefined &&
    under(id).some(
      (p) => pointState(p, readings()!.get(p.id), Date.now()) === "attn",
    );
  const choose = (id: string) => {
    setScope(id);
    setOpen(false);
    setQuery("");
  };
  const onKey = (e: KeyboardEvent) => {
    const target = e.target as HTMLElement | null;
    if (
      e.key === "/" &&
      !open() &&
      !(
        target &&
        (target.isContentEditable ||
          ["INPUT", "TEXTAREA", "SELECT"].includes(target.tagName))
      )
    ) {
      e.preventDefault();
      setOpen(true);
    }
  };
  onMount(() => document.addEventListener("keydown", onKey));
  onCleanup(() => document.removeEventListener("keydown", onKey));
  let search: HTMLInputElement | undefined;
  createEffect(() => {
    if (open()) {
      queueMicrotask(() => search?.focus());
    }
  });

  return (
    <div class="cw" ref={ref}>
      <nav class="crumbs" aria-label={t("scope.label")}>
        <Show
          when={crumbs().length > 0}
          fallback={<span class="hint">{t("scope.none")}</span>}
        >
          <For each={crumbs()}>
            {(n, i) => (
              <>
                <Show when={i() > 0}>
                  <span class="sep">/</span>
                </Show>
                <button
                  type="button"
                  class={i() === crumbs().length - 1 ? "cur" : ""}
                  onClick={() => setScope(n.id)}
                >
                  {n.name}
                </button>
              </>
            )}
          </For>
        </Show>
        <Show when={nodes().length > 0}>
          <button
            type="button"
            class="pick"
            aria-expanded={open()}
            aria-haspopup="dialog"
            title={t("scope.changeKey")}
            onClick={() => setOpen(!open())}
          >
            <Icon name="tree" />
            <Icon name="chevd" />
            <span class="sr-only">{t("scope.change")}</span>
          </button>
        </Show>
      </nav>
      <Show when={open()}>
        <div class="pop" role="dialog" aria-label={t("scope.change")}>
          <h3>{t("scope.treeTitle")}</h3>
          <label class="search">
            <Icon name="search" />
            <input
              ref={search}
              type="search"
              placeholder={t("scope.search")}
              value={query()}
              autocomplete="off"
              onInput={(e) => setQuery(e.currentTarget.value)}
            />
          </label>
          <ul class="tree">
            <For
              each={rows()}
              fallback={<li class="hint">{t("assets.noMatch")}</li>}
            >
              {(r) => (
                <li>
                  <div
                    class={`row node ${r.node.id === scope()?.id ? "sel" : ""}`}
                  >
                    <Guides lasts={r.lasts} />
                    <span class="ico">
                      <NodeKindIcon kind={r.node.kind} />
                    </span>
                    <button
                      type="button"
                      class="nm"
                      aria-current={
                        r.node.id === scope()?.id ? "true" : undefined
                      }
                      onClick={() => choose(r.node.id)}
                    >
                      {r.node.name}
                    </button>
                    <Show when={attention(r.node.id)}>
                      <span
                        class="dot warn"
                        title={t("assets.pointState.attn")}
                      />
                    </Show>
                    <span class="cnt">
                      {t("scope.points", { count: under(r.node.id).length })}
                    </span>
                  </div>
                </li>
              )}
            </For>
          </ul>
          <div class="hint">{t("scope.hint")}</div>
        </div>
      </Show>
    </div>
  );
}

function PeriodPicker() {
  const [open, setOpen, ref] = usePopover();
  return (
    <div class="cw" ref={ref}>
      <div class="rng">
        <button
          type="button"
          class="lbl"
          aria-expanded={open()}
          aria-haspopup="dialog"
          onClick={() => setOpen(!open())}
        >
          <Icon name="calendar" />
          <span>{formatRange(range().from, range().to, locale())}</span>
        </button>
      </div>
      <Show when={open()}>
        <div class="pop right" role="dialog" aria-label={t("period.title")}>
          <h3>{t("period.title")}</h3>
          <div class="presets">
            <For each={PERIODS}>
              {(p: Period) => (
                <button
                  type="button"
                  aria-pressed={period() === p}
                  onClick={() => {
                    setPeriod(p);
                    setOpen(false);
                  }}
                >
                  {t(`period.${p}`)}
                </button>
              )}
            </For>
          </div>
        </div>
      </Show>
    </div>
  );
}

function LiveIndicator() {
  const dot = () =>
    ({
      connected: "dot pulse",
      connecting: "dot warn",
      reconnecting: "dot warn",
      disconnected: "dot crit",
    })[liveState()];
  return (
    <span class="live" title={t(`live.${liveState()}`)}>
      <i class={dot()} />
      {t(`live.${liveState()}`)}
    </span>
  );
}

function ContextBar() {
  const location = useLocation();
  const item = () => current(location.pathname);
  return (
    <div class="ctx">
      <Show
        when={item()?.scoped}
        fallback={
          <span class="space">{t(`nav.${item()?.key ?? "overview"}`)}</span>
        }
      >
        <span class="space">
          <Icon name="site" />
          {t("scope.label")}
        </span>
        <ScopePicker />
      </Show>
      <div class="ctx-right">
        <Show when={item()?.scoped}>
          <PeriodPicker />
        </Show>
        <Bell />
        <LiveIndicator />
      </div>
    </div>
  );
}

function BottomNav() {
  const location = useLocation();
  const [more, setMore] = createSignal(false);
  const inMore = () =>
    !viewProfile().bottom.some((p) => location.pathname.startsWith(p));
  return (
    <>
      <nav class="bottom-nav" aria-label={t("shell.navigation")}>
        <For each={itemsOf(viewProfile().bottom)}>
          {(item) => (
            <A href={item.path}>
              <Icon name={item.icon} />
              <span>{t(`nav.${item.key}`)}</span>
            </A>
          )}
        </For>
        <button
          type="button"
          aria-current={inMore() ? "page" : undefined}
          aria-haspopup="dialog"
          onClick={() => setMore(true)}
        >
          <Icon name="list" />
          <span>{t("shell.more")}</span>
        </button>
      </nav>
      <Show when={more()}>
        <div
          class="ovl sheet"
          onClick={(e) => e.target === e.currentTarget && setMore(false)}
        >
          <div class="dlg" role="dialog" aria-label={t("shell.navigation")}>
            <div class="dlg-b">
              <NavLinks onNavigate={() => setMore(false)} />
              <div class="who">
                <ProfileSelect
                  id="profileSelM"
                  onChanged={() => setMore(false)}
                />
                <label for="langSelM">{t("lang.label")}</label>
                <LanguageSelect id="langSelM" />
                <SignOut />
              </div>
            </div>
          </div>
        </div>
      </Show>
    </>
  );
}

function Layout(props: RouteSectionProps): JSX.Element {
  return (
    <div class="app">
      <Rail />
      <div class="main">
        <ContextBar />
        <main class="work">{props.children}</main>
      </div>
      <BottomNav />
    </div>
  );
}

function SignInPage() {
  return (
    <div class="signin">
      <div class="card">
        <Brand />
        <p class="muted">{t("auth.intro")}</p>
        <a class="btn primary" href={signInUrl()}>
          {t("auth.signIn")}
        </a>
        <div class="field">
          <label for="langSelS">{t("lang.label")}</label>
          <LanguageSelect id="langSelS" />
        </div>
      </div>
    </div>
  );
}

export function App() {
  return (
    <Switch>
      <Match when={session().status === "loading"}>
        <p class="muted">{t("common.loading")}</p>
      </Match>
      <Match when={session().status === "signedOut"}>
        <SignInPage />
      </Match>
      <Match when={session().status === "error"}>
        <main class="work">
          <ErrorBox error={(session() as { error: unknown }).error} />
        </main>
      </Match>
      <Match when={session().status === "signedIn"}>
        <Router root={Layout}>
          <Route
            path="/"
            component={() => <Navigate href={viewProfile().start} />}
          />
          <Route path="/overview" component={OverviewPage} />
          <Route path="/flows" component={FlowsPage} />
          <Route path="/events" component={EventsPage} />
          <Route path="/events/:id" component={EventPage} />
          <Route path="/commissioning" component={CommissioningPage} />
          <Route path="/plans" component={PlansPage} />
          <Route path="/reports" component={ReportsPage} />
          <Route path="/finance" component={FinancePage} />
          <Route path="/optimization" component={OptimizationPage} />
          <Route path="/users" component={UsersPage} />
          <Route path="/profile" component={ProfilePage} />
          <Route path="/places" component={PlacesPage} />
          <Route path="/automations" component={AutomationsPage} />
          <Route path="/cluster" component={ClusterPage} />
          <Route path="/charging" component={ChargingPage} />
          <Route path="/analysis" component={AnalysisPage} />
          <Route path="/assets" component={AssetsPage} />
          <Route path="/devices/:id?" component={DevicesPage} />
          <Route path="/integrations/:id?" component={ConnectionsPage} />
          <Route
            path="/connections"
            component={() => <Navigate href="/integrations" />}
          />
          <Route path="/extensions" component={ExtensionsPage} />
          <Route path="/catalog" component={CatalogPage} />
          <Route
            path="*"
            component={() => <Navigate href={viewProfile().start} />}
          />
        </Router>
      </Match>
    </Switch>
  );
}
